import { FC, ReactNode } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import InstagramIcon from "@mui/icons-material/Instagram";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useNavigate } from "react-router-dom";
import { INSTAGRAM_URL, SUPPORT_EMAIL, WHATSAPP_URL } from "../support-data";
import { isCleoEnabled } from "../../../../services/feature-flags.service";

type Channel = {
  key: string;
  iconBg: string;
  icon: ReactNode;
  name: string;
  sub: string;
  cta: string;
  ctaColor: string;
  onClick: () => void;
};

/** Cartões de canais de atendimento (WhatsApp / E-mail / Cléo). */
const SupportChannels: FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const channels: Channel[] = [
    {
      key: "whatsapp",
      iconBg: theme.palette.brand.successBg,
      icon: <WhatsAppIcon sx={{ color: "#25D366", fontSize: 28 }} />,
      name: "WhatsApp",
      sub: "(51) 9391-4753",
      cta: "Conversar →",
      ctaColor: theme.palette.success.main,
      onClick: () => window.open(WHATSAPP_URL, "_blank"),
    },
    {
      key: "email",
      iconBg: theme.palette.brand.cream,
      icon: <MailOutlineIcon sx={{ color: "primary.main", fontSize: 28 }} />,
      name: "E-mail",
      sub: SUPPORT_EMAIL,
      cta: "Enviar e-mail →",
      ctaColor: theme.palette.primary.main,
      onClick: () => window.open(`mailto:${SUPPORT_EMAIL}`),
    },
    {
      key: "instagram",
      iconBg: theme.palette.brand.cream,
      icon: <InstagramIcon sx={{ color: "#E1306C", fontSize: 28 }} />,
      name: "Instagram",
      sub: "@plataformaproativos",
      cta: "Seguir →",
      ctaColor: theme.palette.primary.main,
      onClick: () => window.open(INSTAGRAM_URL, "_blank"),
    },
    // Cléo só aparece quando a feature flag está ligada (a rota /dashboard/cleo
    // também é gated — sem isto o card levaria a um redirect para /dashboard/app).
    ...(isCleoEnabled()
      ? [
          {
            key: "cleo",
            iconBg: theme.palette.brand.heroMobile,
            icon: <AutoAwesomeIcon sx={{ color: "#fff", fontSize: 28 }} />,
            name: "Perguntar à Cléo",
            sub: "Tire dúvidas na hora",
            cta: "Abrir Cléo →",
            ctaColor: theme.palette.primary.main,
            onClick: () => navigate("/dashboard/cleo"),
          } as Channel,
        ]
      : []),
  ];

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: `repeat(${Math.min(channels.length, 3)}, minmax(0, 1fr))` },
        gap: 2,
        maxWidth: 860,
        mx: "auto",
        mb: 4,
      }}
    >
      {channels.map((ch) => (
        <Box
          key={ch.key}
          onClick={ch.onClick}
          sx={{
            height: "100%",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            backgroundColor: "background.paper",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "16px",
            p: "22px",
            cursor: "pointer",
            transition: "box-shadow 0.18s, border-color 0.18s",
            "&:hover": { borderColor: theme.palette.brand.creamStroke, boxShadow: theme.customShadows.card },
          }}
        >
          <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 48, height: 48, borderRadius: "12px", background: ch.iconBg, mb: 1.5 }}>
            {ch.icon}
          </Box>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: "text.primary", mb: 0.5 }}>{ch.name}</Typography>
          <Typography sx={{ fontSize: ch.key === "email" ? 11 : 13, color: "text.secondary", mb: 1.5, whiteSpace: ch.key === "email" ? "nowrap" : "normal" }}>{ch.sub}</Typography>
          <Typography sx={{ mt: "auto", fontSize: 13.5, fontWeight: 600, color: ch.ctaColor }}>{ch.cta}</Typography>
        </Box>
      ))}
    </Box>
  );
};

export default SupportChannels;
